import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { corporateCMSApi, post, put } from "~/Services";
import { corporateHost } from "~/Utils";

import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Typography, Upload } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { fields, fieldsReady } from "../constants";
import { useGrandChildMenus } from "../MainNavigation.hook";
export const AddEditGrandChildMenus = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [fieldData, setFieldData] = useState<any>();

  async function fetchData() {
    await fieldsReady;

    setFieldData(fields);
  }
  useEffect(() => {
    fetchData();
  }, []);

  const [uploadModal, setUploadModal] = useState<{
    visible: boolean;
    fieldName?: string;
    file?: RcFile;
    uploading?: boolean;
    setValue?: (name: string, value: string) => void;
  }>({
    visible: false,
    fieldName: undefined,
    file: undefined,
    uploading: false,
  });
  const [childList, setChildList] = useState<any[]>([]);
  const getChildRecords = () => {
    let url = `/api/get-child-menu/allfields`;
    corporateCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          setChildList(res.data); // store the array, not length
        }
      })
      .catch((err) => {
        setChildList([]);
      });
  };

  useEffect(() => {
    getChildRecords();
  }, []);

  const getIdByName = (name: string) => {
    const item = childList.find((entry: any) => entry.id === name);
    return item ? item.name_en : null;
  };

  const {
    data: editData,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchEditData,
  } = useGrandChildMenus(query.get("editId") || "");

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEditData();
    }
  }, [query.get("editId"), query.get("id")]);

  const GrandChildMenuFormConfig: FormConfig = {
    initialValues: {
      parent_name: dataById ? dataById.payload.parent_name : editData ? editData?.parent_name : "",
      parent_id: dataById ? dataById.payload.parent_id : editData ? editData?.parent_id : "",

      name_en: dataById ? decodeHTML(dataById.payload.name_en) : editData ? decodeHTML(editData?.name_en) : "",

      name_ms: dataById ? decodeHTML(dataById.payload.name_ms) : editData ? decodeHTML(editData?.name_ms) : "",

      name_zh: dataById ? decodeHTML(dataById.payload.name_zh) : editData ? decodeHTML(editData?.name_zh) : "",

      redirect_url_en: dataById
        ? decodeHTML(dataById.payload.redirect_url_en)
        : editData
          ? decodeHTML(editData?.redirect_url_en)
          : "",

      redirect_url_ms: dataById
        ? decodeHTML(dataById.payload.redirect_url_ms)
        : editData
          ? decodeHTML(editData?.redirect_url_ms)
          : "",

      redirect_url_zh: dataById
        ? decodeHTML(dataById.payload.redirect_url_zh)
        : editData
          ? decodeHTML(editData?.redirect_url_zh)
          : "",

      published: dataById ? dataById.payload.published : editData ? editData.published : false, // default to false if no data
    },

    fields: fieldData?.grandChildMenus?.map((field: any) => {
      if (field.uploadTrigger) {
        return {
          ...field,
          openUpload: (fieldName: string, setValue: (name: string, value: string) => void) => {
            setUploadModal({
              visible: true,
              fieldName,
              setValue,
              file: undefined,
              uploading: false,
            });
          },
          usedFileType: ".pdf",
        };
      }
      return field;
    }),
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["field_news_images"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CORPORATE");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }
    let dateKeys: any = ["date"];
    for (const key of dateKeys) {
      if (data[key]) {
        data[key] = moment(data[key]).format("DD MMM YYYY");
      }
    }
    if (data.parent_id) {
      const parentId = getIdByName(data.parent_id);
      if (parentId) {
        data.parent_name = parentId;
      }
    }

    const payload = {
      action: action,
      method: method,
      module: "Navigation",
      subModule: "grandChildMenus",
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "MAHB_CORPORATE_Navigation",
      payload: {
        ...(action === "UPDATE" && { id: editData?.id || dataById?.payload?.id }),
        ...data,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingData}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add"} Grand Child Menu`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingData || drilldownLoading}
        config={GrandChildMenuFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${corporateHost}/api/get-grand-child-menu-items-taxonomy-data-list/${editData?.id || dataById?.payload?.id}` : `${corporateHost}/api/create-grand-child-menu-items-taxonomy-data-list`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Navigation"
      />
      {/* ------------------- UPLOAD POPUP ------------------- */}
      <Modal
        title="Redirect URL Guide"
        open={uploadModal.visible}
        footer={null}
        onCancel={() => setUploadModal({ ...uploadModal, visible: false, file: undefined })}
        width={520}
      >
        <Typography.Paragraph style={{ marginBottom: 12 }}>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              Use <code>/example</code> for pages within this application.
            </li>
            <li>
              Use a full link for external websites (example: <code>https://example.com</code>).
            </li>
            <li>
              Want to use a file? Click <strong>Upload</strong> and paste the link.
            </li>
          </ol>
        </Typography.Paragraph>

        <Upload.Dragger
          accept="*"
          multiple={false}
          showUploadList={false}
          beforeUpload={(file) => {
            setUploadModal((s) => ({ ...s, file }));
            return false;
          }}
        >
          {uploadModal.file ? (
            <p className="ant-upload-text">{uploadModal.file.name}</p>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file here</p>
            </>
          )}
        </Upload.Dragger>

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Button
            type="primary"
            disabled={!uploadModal.file || uploadModal.uploading}
            onClick={async () => {
              if (!uploadModal.file || !uploadModal.fieldName || !uploadModal.setValue) return;

              setUploadModal((s) => ({ ...s, uploading: true }));

              const formData = new FormData();
              formData.append("upload-file", uploadModal.file);
              formData.append("application", "CORPORATE");

              try {
                const res: any = await post("management/upload-file", formData);
                const url = res?.response?.data;
                if (url) {
                  uploadModal.setValue(uploadModal.fieldName, url);
                  openToast({ content: `File uploaded: ${url}`, type: "success" });
                }
              } catch (e) {
                openToast({ content: "Upload failed", type: "error" });
              } finally {
                setUploadModal({ visible: false, fieldName: undefined, file: undefined, uploading: false });
              }
            }}
          >
            {uploadModal.uploading ? (
              <>
                <LoadingOutlined /> Uploading…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </Modal>
    </ContentWrapper>
  );
};
